import React, { useState, useMemo } from 'react';
import { DiscoveryAgentCard } from './DiscoveryAgentCard';
import { AgentSearch } from './AgentSearch';
import { CategoryFilter } from './CategoryFilter';
import { agentRegistry } from '../../data/agentRegistry';
import type { AgentMetadata } from '../../types/agent.types';

interface AgentDiscoveryHubProps {
  onAgentSelect: (agent: AgentMetadata) => void;
  className?: string;
}

/**
 * AgentDiscoveryHub - Central discovery interface for all platform agents
 * MVP-UX-002: Agent Discovery Hub (5 points, P0 BLOCKS ADOPTION)
 * 
 * Features:
 * - Browse all 35+ agents with rich metadata
 * - Filter by category, epic, capabilities
 * - Search across name, description, tags
 * - Responsive grid layout
 * - Backend API integration ready (/api/agents/discovery)
 */
export function AgentDiscoveryHub({ onAgentSelect, className = '' }: AgentDiscoveryHubProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(false);

  // MVP-UX-002: Enhanced filtering with backend API integration option
  const filteredAgents = useMemo(() => {
    return agentRegistry.filter(agent => {
      const matchesSearch = !searchQuery || 
        agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        agent.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        agent.epic?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        agent.capabilities.some(cap => cap.toLowerCase().includes(searchQuery.toLowerCase()));
      
      const matchesCategory = selectedCategory === 'all' || agent.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, selectedCategory]);

  // Get unique categories
  const categories = useMemo(() => {
    const cats = ['all', ...new Set(agentRegistry.map(agent => agent.category))];
    return cats;
  }, []);

  const handleAgentClick = async (agent: AgentMetadata) => {
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 100)); // Simulate loading
      onAgentSelect(agent);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`w-full max-w-6xl mx-auto p-6 space-y-6 ${className}`}>
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          AI Capabilities
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-300">
          What would you like to create or accomplish today?
        </p>
      </div>

      {/* Search and Filters */}
      <div className="space-y-4">
        <AgentSearch
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="What would you like to do? (e.g., write email, analyze market, create content...)"
        />
        
        <CategoryFilter
          categories={categories}
          selectedCategory={selectedCategory}
          onCategorySelect={setSelectedCategory}
        />
      </div>

      {/* Results Count - Task-focused messaging */}
      <div className="text-sm text-gray-600 dark:text-gray-400">
        {filteredAgents.length} capabilities available
        {selectedCategory !== 'all' && ` in ${selectedCategory}`}
      </div>

      {/* Agent Grid */}
      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAgents.map(agent => (
            <DiscoveryAgentCard
              key={agent.id}
              agent={agent}
              onClick={() => handleAgentClick(agent)}
            />
          ))}
        </div>
      )}

      {/* No Results */}
      {filteredAgents.length === 0 && !isLoading && (
        <div className="text-center py-12 space-y-4">
          <div className="text-gray-400 text-6xl">🔍</div>
          <h3 className="text-xl font-medium text-gray-900 dark:text-white">
            No matching capabilities found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Try describing what you want to accomplish differently
          </p>
        </div>
      )}
    </div>
  );
}